import React from "react";
import GenericTable from "./GenericTable";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Box, Chip, IconButton, Stack, Switch, Tooltip, Typography } from "@mui/material";
import {
  findIntegrationCardByKey,
  inferIntegrationTypeKey,
} from "../constants/integrationCatalog";

const IntegrationTable = ({
  integrations = [],
  rowCount = 0,
  page = 0,                 // zero-based
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  onSearch,
  isLoading = false,
  onEdit,
  onDelete,
  onToggleStatus,
  onDeleteSelected,
  onSelectionChange,
  paginationMode = "server",
}) => {
  const ordered = integrations
    .slice()
    .sort((a, b) => (Number(a?.ordem) || 0) - (Number(b?.ordem) || 0));

  const rows = ordered.map((it) => ({
    id: it.id,
    typeKey: inferIntegrationTypeKey(it),
    nome: it.nome ?? "",
    provedor: it.provedor || "-",
    statusRaw: Number(it.status) === 1 ? 1 : it.status ? 1 : 0,
    __virtual: !!it.__virtual,
  }));

  const columns = [
    {
      field: "typeKey",
      headerName: "Tipo",
      width: 220,
      renderCell: (params) => {
        const card = findIntegrationCardByKey(params.value);
        const Icon = card.icon;
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Icon />
            <Typography variant="body2" title={card.label}>
              {card.label}
            </Typography>
          </Stack>
        );
      },
    },
    { field: "nome", headerName: "Nome", flex: 1.4, minWidth: 220 },
    {
      field: "provedor",
      headerName: "Provedor",
      width: 160,
      renderCell: (params) => <Chip size="small" label={params.value || "-"} />,
    },
    {
      field: "status",
      headerName: "Status",
      width: 190,
      sortable: false,
      renderCell: (params) => {
        const full =
          ordered.find((r) => String(r.id) === String(params.row.id)) || params.row;
        const isActive = Number(params.row.statusRaw) === 1;
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              size="small"
              checked={isActive}
              disabled={!onToggleStatus}
              onChange={() => onToggleStatus?.(full)}
            />
            <Chip
              size="small"
              label={isActive ? "Ativa" : "Inativa"}
              color={isActive ? "success" : "default"}
              variant="outlined"
            />
          </Stack>
        );
      },
    },
    {
      field: "actions",
      headerName: "Ações",
      width: 140,
      sortable: false,
      renderCell: (params) => {
        const full =
          ordered.find((r) => String(r.id) === String(params.row.id)) || params.row;
        return (
          <Box display="flex" gap={1} alignItems="center" sx={{ height: "100%" }}>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => onEdit?.(full)}>
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir">
              <IconButton size="small" onClick={() => onDelete?.(params.row.id)}>
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <GenericTable
      rows={rows}
      columns={columns}
      rowCount={rowCount}
      pageSize={pageSize}
      page={page}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSearch={onSearch}
      isLoading={isLoading}
      exportFileName="integracoes"
      onDeleteSelected={onDeleteSelected}
      onSelectionChange={onSelectionChange}
      paginationMode={paginationMode}
      isRowSelectable={(params) => !String(params?.id || "").startsWith("passagens:")}
      onRowClick={(row) => {
        const full = ordered.find((r) => String(r.id) === String(row.id)) || row;
        onEdit?.(full);
      }}
    />
  );
};

export default IntegrationTable;
